import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Pencil as Edit2, Copy, Check, Mail, Loader2, Eye } from 'lucide-react';
import { VariablePill, parseContentWithVariables } from './VariablePill';
import { AIRegenerateButton } from './AIRegenerateButton';
import { VariableInsertPanel } from './VariableInsertPanel';
import type { TemplateVariable } from '../../hooks/useVariableInsertion';
import type { TemplateStatus } from '../../services/templateService';

interface EmailPreviewCardProps {
  title: string;
  subject: string;
  content: string;
  senderName?: string;
  senderEmail?: string;
  placeholder?: string;
  subjectPlaceholder?: string;
  onUpdate: (subject: string, content: string) => void;
  onGenerateSubject?: (tone: string, length: string, currentContent: string) => Promise<string>;
  onGenerateBody?: (tone: string, length: string, currentContent: string) => Promise<string>;
  onDirtyChange?: (isDirty: boolean) => void;
  maxLength?: number;
  defaultExpanded?: boolean;
  variables?: TemplateVariable[];
  templateStatus?: TemplateStatus;
  onApproveTemplate?: (subject: string, body: string) => Promise<void>;
  approvingTemplate?: boolean;
  readOnly?: boolean;
}

export function EmailPreviewCard({
  title,
  subject,
  content,
  senderName = 'Adam Ray',
  senderEmail = 'adam@salespad.app',
  placeholder,
  subjectPlaceholder,
  onUpdate,
  onGenerateSubject,
  onGenerateBody,
  onDirtyChange,
  maxLength,
  defaultExpanded = false,
  variables,
  templateStatus,
  onApproveTemplate,
  approvingTemplate,
  readOnly = false,
}: EmailPreviewCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);
  const [editSubject, setEditSubject] = useState(subject);
  const [editContent, setEditContent] = useState(content);
  const [copied, setCopied] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [insertedVar, setInsertedVar] = useState<string | null>(null);

  const subjectInputRef = useRef<HTMLInputElement>(null);
  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef = useRef<number>(0);
  const activeFieldRef = useRef<'subject' | 'body'>('body');

  const trackCursor = useCallback((field: 'subject' | 'body') => {
    activeFieldRef.current = field;
    const el = field === 'subject' ? subjectInputRef.current : bodyTextareaRef.current;
    if (el) {
      cursorPosRef.current = el.selectionStart ?? 0;
    }
  }, []);

  const handleInsertVariable = useCallback((varName: string) => {
    const field = activeFieldRef.current;
    const el = field === 'subject' ? subjectInputRef.current : bodyTextareaRef.current;
    const currentContent = field === 'subject' ? editSubject : editContent;
    const pos = cursorPosRef.current;
    const before = currentContent.slice(0, pos);
    const after = currentContent.slice(pos);
    const newContent = before + varName + after;

    if (field === 'subject') {
      setEditSubject(newContent);
    } else {
      setEditContent(newContent);
    }

    setInsertedVar(varName);
    setTimeout(() => setInsertedVar(null), 600);

    requestAnimationFrame(() => {
      if (el) {
        const newPos = pos + varName.length;
        el.focus();
        el.setSelectionRange(newPos, newPos);
        cursorPosRef.current = newPos;
      }
    });
  }, [editSubject, editContent]);

  useEffect(() => {
    if (isEditing) {
      onDirtyChange?.(editSubject !== subject || editContent !== content);
    }
  }, [isEditing, editSubject, editContent, subject, content]);

  const handleCopy = () => {
    navigator.clipboard.writeText(`Subject: ${subject}\n\n${content}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    onUpdate(editSubject, editContent);
    if (onApproveTemplate) {
      await onApproveTemplate(editSubject, editContent);
    }
    setIsEditing(false);
    setShowVariables(false);
    onDirtyChange?.(false);
  };

  const handleCancel = () => {
    setEditSubject(subject);
    setEditContent(content);
    setIsEditing(false);
    setShowVariables(false);
    onDirtyChange?.(false);
  };

  const handleAcceptGeneratedSubject = (generatedSubject: string) => {
    setEditSubject(generatedSubject);
    onUpdate(generatedSubject, editContent);
  };

  const handleAcceptGeneratedBody = (generatedBody: string) => {
    setEditContent(generatedBody);
    onUpdate(editSubject, generatedBody);
  };

  const charCount = editContent.length;
  const isNearLimit = maxLength && charCount > maxLength * 0.9;
  const isOverLimit = maxLength && charCount > maxLength;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderEmailPreview = () => {
    if (!content && !subject) {
      return (
        <div className="text-sm text-gray-400 italic p-4 bg-gray-50 rounded-lg">
          {placeholder || 'No email configured yet'}
        </div>
      );
    }

    return (
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1875FF] to-[#05E0D1] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
            {getInitials(senderName)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900 text-sm">{senderName}</p>
                <p className="text-xs text-gray-500">{senderEmail}</p>
              </div>
              <span className="text-xs text-gray-500">2 min ago</span>
            </div>
          </div>
        </div>

        <div className="mb-3 pb-3 border-b border-gray-200">
          <p className="text-xs text-gray-600 mb-1">Subject:</p>
          <div className="font-semibold text-gray-900 text-sm">
            {subject ? (
              parseContentWithVariables(subject).map((part, idx) => {
                if (part.type === 'variable') {
                  return (
                    <VariablePill
                      key={idx}
                      variable={part.content}
                      type={part.variableType}
                    />
                  );
                }
                return <span key={idx}>{part.content}</span>;
              })
            ) : (
              <span className="text-gray-400 italic">No subject</span>
            )}
          </div>
        </div>

        <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
          {content ? (
            parseContentWithVariables(content).map((part, idx) => {
              if (part.type === 'variable') {
                return (
                  <VariablePill
                    key={idx}
                    variable={part.content}
                    type={part.variableType}
                  />
                );
              }
              return <span key={idx}>{part.content}</span>;
            })
          ) : (
            <span className="text-gray-400 italic">No content</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
      <div
        className="p-5 cursor-pointer"
        onClick={() => !isEditing && setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
            />
            <Mail className="w-5 h-5 text-[#1875FF]" />
            <h3 className="text-base font-bold text-gray-900 font-display">{title}</h3>
            {templateStatus === 'approved' ? (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                Configured
              </span>
            ) : templateStatus === 'draft' ? (
              <span className="px-2.5 py-0.5 bg-[#05E0D1]/10 text-[#0bb5a5] text-xs font-semibold rounded-full flex items-center gap-1.5 border border-[#05E0D1]/25">
                Draft - Review Pending
                <Eye className="w-3 h-3" />
              </span>
            ) : !content && !subject ? (
              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-medium rounded-full">
                Not Configured
              </span>
            ) : (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                Configured
              </span>
            )}
          </div>

          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {!readOnly && (
              <button
                onClick={() => {
                  setEditSubject(subject);
                  setEditContent(content);
                  setIsEditing(true);
                  setIsExpanded(true);
                }}
                className="p-2 text-gray-400 hover:text-[#1875FF] hover:bg-[#1875FF]/10 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={handleCopy}
              disabled={!content && !subject}
              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-30"
              title="Copy"
            >
              {copied ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 pb-5 border-t border-gray-100">
          {isEditing && !readOnly ? (
            <div className="space-y-3 pt-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Subject Line
                  </label>
                  {onGenerateSubject && (
                    <AIRegenerateButton
                      onRegenerate={onGenerateSubject}
                      onAccept={handleAcceptGeneratedSubject}
                      currentContent={editSubject}
                    />
                  )}
                </div>
                <input
                  ref={variables ? subjectInputRef : undefined}
                  type="text"
                  value={editSubject}
                  onChange={(e) => {
                    setEditSubject(e.target.value);
                    if (variables) trackCursor('subject');
                  }}
                  onSelect={variables ? () => trackCursor('subject') : undefined}
                  onClick={variables ? () => trackCursor('subject') : undefined}
                  onKeyUp={variables ? () => trackCursor('subject') : undefined}
                  placeholder={subjectPlaceholder || 'Enter email subject...'}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-gray-700">
                    Email Body
                  </label>
                  {onGenerateBody && (
                    <AIRegenerateButton
                      onRegenerate={onGenerateBody}
                      onAccept={handleAcceptGeneratedBody}
                      currentContent={editContent}
                    />
                  )}
                </div>
                <textarea
                  ref={variables ? bodyTextareaRef : undefined}
                  value={editContent}
                  onChange={(e) => {
                    setEditContent(e.target.value);
                    if (variables) trackCursor('body');
                  }}
                  onSelect={variables ? () => trackCursor('body') : undefined}
                  onClick={variables ? () => trackCursor('body') : undefined}
                  onKeyUp={variables ? () => trackCursor('body') : undefined}
                  placeholder={placeholder}
                  rows={10}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] text-sm resize-none ${
                    isOverLimit ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {maxLength && (
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs text-gray-500">
                      <span
                        className={
                          isOverLimit
                            ? 'text-red-600 font-medium'
                            : isNearLimit
                            ? 'text-yellow-600 font-medium'
                            : ''
                        }
                      >
                        {charCount}
                      </span>
                      {' / '}
                      {maxLength} characters
                    </p>
                  </div>
                )}
              </div>

              {variables && (
                <VariableInsertPanel
                  variables={variables}
                  showVariables={showVariables}
                  onToggle={() => setShowVariables(!showVariables)}
                  onInsert={handleInsertVariable}
                  insertedVar={insertedVar}
                />
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isOverLimit || approvingTemplate}
                  className="px-4 py-2 bg-gradient-to-br from-[#1875FF] to-[#05E0D1] text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm font-medium flex items-center gap-2"
                >
                  {approvingTemplate && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Changes
                </button>
              </div>
            </div>
          ) : (
            <div className="pt-4">{renderEmailPreview()}</div>
          )}
        </div>
      )}
    </div>
  );
}
